import AppointmentSetterSvg from '@forwardflow/assets/svg/AppointmentSetterSvg'
import ConversationalistSvg from '@forwardflow/assets/svg/ConversationalistSvg'
import DealCloserSvg from '@forwardflow/assets/svg/DealCloserSvg'
import StrugglingToConnectSvg from '@forwardflow/assets/svg/StrugglingToConnectSvg'
import TeamPerformanceSvg from '@forwardflow/assets/svg/TeamPerformanceSvg'
import TextMessengerSvg from '@forwardflow/assets/svg/TextMessengerSvg'
import { useGetDashboardChartsTeamPerformance } from '@forwardflow/hooks/customHooks'
import { TimePeriod } from '@forwardflow/types'
import { useSearchParams } from 'react-router-dom'

const getInitials = (name: string) =>
   name
      ?.split(' ')
      ?.map((n) => n[0])
      ?.join('')
      ?.toUpperCase()

const PanelCard = ({
   icon,
   title,
   name,
   value,
   initials,
   color,
}: {
   icon: React.ReactNode
   title: string
   value: string
   name?: string
   initials: string
   color?: string
}) => (
   <div className="flex flex-col gap-2 border rounded-lg bg-white/5 border-white/10">
      <div className="flex items-center gap-4 px-5 py-2 mb-1 font-medium text-white border-b border-white/10 h-14">
         <div className="flex items-center justify-center flex-shrink-0 w-10 h-10">
            {icon}
         </div>
         <span>{title}</span>
      </div>
      <div className="flex items-center gap-4 px-5 pt-0 pb-4">
         <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 font-medium text-white rounded-full bg-black/20">
            {initials}
         </div>
         <div>
            <div className="mb-1 text-lg font-semibold">{name}</div>
            <div className={`text-sm ${color ?? 'text-gray-300'}`}>{value}</div>
         </div>
      </div>
   </div>
)

export const AgentPerformancePanel = () => {
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const {
      data: DashboardChartsOverview,
      isLoading: DashboardChartsOverviewLoading,
   } = useGetDashboardChartsTeamPerformance({
      timeFrame: timePeriod as TimePeriod,
   })

   const best = DashboardChartsOverview?.bestPerformers
   const worst = DashboardChartsOverview?.worstPerformers

   if (DashboardChartsOverviewLoading) {
      return (
         <div className="flex items-center justify-center h-64">
            <span className="text-white">Loading...</span>
         </div>
      )
   }

   return (
      <div className="flex flex-col w-full overflow-hidden lg:flex-row rounded-2xl">
         <div className="flex-1 bg-gradient-to-r from-[#4FA3FF]/10 to-[#2BE0A2]/10 p-4 md:px-12 md:py-4">
            <div className="flex items-center gap-4 mb-4 md:mb-4">
               <TeamPerformanceSvg />
               <span className="text-lg font-semibold text-white">
                  Best Performing Agents
               </span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-4">
               <PanelCard
                  icon={<DealCloserSvg />}
                  title="Deal Closer"
                  value={best?.dealCloser?.stats ?? ''}
                  initials={getInitials(best?.dealCloser?.agent ?? '')}
                  name={best?.dealCloser?.agent ?? ''}
                  color="text-[#3CFFE0]"
               />
               <PanelCard
                  icon={<AppointmentSetterSvg />}
                  title="Appointment Setter"
                  value={best?.appointmentSetter?.stats ?? ''}
                  initials={getInitials(best?.appointmentSetter?.agent ?? '')}
                  name={best?.appointmentSetter?.agent ?? ''}
                  color="text-[#3CFFE0]"
               />
               <PanelCard
                  icon={<ConversationalistSvg />}
                  title="Conversationalist"
                  value={best?.conversationalist?.stats ?? ''}
                  initials={getInitials(best?.conversationalist?.agent ?? '')}
                  name={best?.conversationalist?.agent ?? ''}
                  color="text-[#3CFFE0]"
               />
               <PanelCard
                  icon={<TextMessengerSvg />}
                  title="Text Messenger"
                  value={best?.textMessenger?.stats ?? ''}
                  initials={getInitials(best?.textMessenger?.agent ?? '')}
                  name={best?.textMessenger?.agent ?? ''}
                  color="text-[#3CFFE0]"
               />
            </div>
         </div>

         <div className="flex-1 bg-gradient-to-r from-[#9061DA]/10 to-[#FF6D99]/10 p-4 md:px-12 md:py-4">
            <div className="flex items-center gap-4 mb-4 md:mb-4">
               <StrugglingToConnectSvg />
               <span className="text-lg font-semibold text-white">
                  Worst Performing Agents
               </span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-4">
               <PanelCard
                  icon={<DealCloserSvg />}
                  title="Deal Closer"
                  value={worst?.dealCloser?.stats ?? ''}
                  initials={getInitials(worst?.dealCloser?.agent ?? '')}
                  name={worst?.dealCloser?.agent ?? ''}
                  color="text-[#FF849F]"
               />
               <PanelCard
                  icon={<AppointmentSetterSvg />}
                  title="Appointment Setter"
                  value={worst?.appointmentSetter?.stats ?? ''}
                  initials={getInitials(worst?.appointmentSetter?.agent ?? '')}
                  name={worst?.appointmentSetter?.agent ?? ''}
                  color="text-[#FF849F]"
               />
               <PanelCard
                  icon={<ConversationalistSvg />}
                  title="Conversationalist"
                  value={worst?.conversationalist?.stats ?? ''}
                  initials={getInitials(worst?.conversationalist?.agent ?? '')}
                  name={worst?.conversationalist?.agent ?? ''}
                  color="text-[#FF849F]"
               />
               <PanelCard
                  icon={<TextMessengerSvg />}
                  title="Text Messenger"
                  value={worst?.textMessenger?.stats ?? ''}
                  initials={getInitials(worst?.textMessenger?.agent ?? '')}
                  name={worst?.textMessenger?.agent ?? ''}
                  color="text-[#FF849F]"
               />
            </div>
         </div>
      </div>
   )
}
